class Api {
  constructor() {
    // Normalize base and force localhost when the app is served from localhost
    const normalizeBase = (raw) => {
      if (!raw) return null;
      const trimmed = raw.replace(/\/+$/, '');
      return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
    };

    // Try to get runtime config from docker-entrypoint.sh generated config.js
    let runtimeConfig = null;
    try {
      if (window.ENV && window.ENV.API_BASE_URL) {
        runtimeConfig = window.ENV.API_BASE_URL;
      }
    } catch (e) {
      // Window not available
    }

    const envBase = normalizeBase(import.meta.env.VITE_API_BASE_URL) || normalizeBase(runtimeConfig);
    
    // Runtime hostname detection (works even if env var wasn't set at build time)
    let hostname = '';
    let origin = '';
    try {
      hostname = window?.location?.hostname || '';
      origin = window?.location?.origin || '';
    } catch (e) {
      // Window not available (SSR)
    }
    
    // Check if we're on localhost (explicit check)
    const isLocalhost = hostname === 'localhost' || 
                       hostname === '127.0.0.1' || 
                       hostname === '' || 
                       hostname.startsWith('192.168.') || 
                       hostname.startsWith('10.') ||
                       origin.includes('localhost') ||
                       origin.includes('127.0.0.1');
    
    // Check if we're on production domain (explicit check)
    const isProduction = hostname.includes('tvkparty.org') || 
                        hostname === 'admin.tvkparty.org' || 
                        hostname === 'join.tvkparty.org' ||
                        origin.includes('tvkparty.org');
    
    // Priority: 1. VITE_API_BASE_URL env var (if set and not localhost), 2. Production domain check, 3. Localhost fallback, 4. Default to production
    if (envBase && !envBase.includes('localhost') && !envBase.includes('127.0.0.1')) {
      // Use env var if it's set and not pointing to localhost
      this.baseUrl = envBase;
    } else if (isProduction) {
      // We're on production domain, use production API
      this.baseUrl = 'https://api.tvkparty.org/api';
    } else if (isLocalhost) {
      // We're on localhost, use local API
      this.baseUrl = 'http://localhost:8788/api';
    } else {
      // Default to production API (safer default for deployed apps)
      this.baseUrl = 'https://api.tvkparty.org/api';
    }

    console.log(`[API] Base URL: ${this.baseUrl}`);
    console.log(`[API] Hostname: ${hostname}`);
    console.log(`[API] Origin: ${origin}`);
    console.log(`[API] Is Localhost: ${isLocalhost}`);
    console.log(`[API] Is Production: ${isProduction}`);
    console.log(`[API] VITE_API_BASE_URL env: ${import.meta.env.VITE_API_BASE_URL}`);
    console.log(`[API] Runtime config: ${runtimeConfig}`);
    this.token = localStorage.getItem('token');
    this.bridge = {
      getToken: () => "",
      getRefresh: () => "",
      setTokens: () => {},
      setRole: () => {},
      setRefreshing: () => {},
      onUnauthorized: () => {},
    };
  }
  initialize(bridge) {
    this.bridge = bridge;
  }
  
  async fetch(path, options = {}, retried = false) {
    let { body, rawResponse } = options;
    let url = `${this.baseUrl}${path}`;
    console.log(`[API] ${options.method || 'GET'} ${url}`);
    const headers = { ...options.headers };
    const method = options.method || 'GET';
  
    if (!(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    if (method === 'GET' && body) {
      url += '?' + new URLSearchParams(body).toString();
      body = undefined;
    }

    const token = this.bridge.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const fetchOptions = {
      ...options,
      headers,
      credentials: 'include',
      body: body instanceof FormData ? body : JSON.stringify(body),
    };
  
    try {
      const response = await fetch(url, fetchOptions);
  
      if (response.status === 401) {
        if (retried || path === '/auth/refresh') {
          this.bridge.onUnauthorized();
          const err = new Error("Unauthorized");
          err.status = 401;
          throw err;
        }
        await this.bridge.refresh();
        return this.fetch(path, options, true);
      }
  
      if (rawResponse) {
        return response;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        const error = new Error(errorData.error || errorData.message || 'Request failed');
        error.status = response.status;
        error.data = errorData;
        console.error(`API Fetch Error (${path}):`, error);
        throw error;
      }
  
      return response.json();
    } catch (error) {
      if (error.status !== 401) { // 401s are handled above, don't double-log
        console.error(`API Fetch Error (${path}):`, error);
      }
      throw error;
    }
  }
}
const api = new Api();
export const apiFetch = api.fetch.bind(api);
export const initializeApi = api.initialize.bind(api);

export const generate2FASecret = () => api.fetch('/auth/generate-2fa-secret', { method: 'POST' });
export const verify2FAToken = (token) => api.fetch('/auth/verify-2fa', { method: 'POST', body: { token } });
export const toggle2FA = (enable, token) => api.fetch('/auth/toggle-2fa', { method: 'POST', body: { enable, token } });